/**
 * Shared plumbing for the third-party marketing trackers (Awin, Masslytics).
 *
 * Two concerns both trackers have in common:
 * - they may only touch the network once the visitor has granted Cookiebot
 *   marketing consent;
 * - each conversion must be reported at most once per user, surviving a refresh
 *   or a component re-render.
 */

declare global {
  interface Window {
    Cookiebot?: { consent?: { marketing?: boolean } };
  }
}

/**
 * True only when the visitor has actively granted Cookiebot marketing consent.
 * False while Cookiebot has not loaded yet or consent was declined, so callers
 * that see `false` must be safe to retry on a later visit.
 */
export function hasMarketingConsent() {
  return typeof window !== "undefined" && window.Cookiebot?.consent?.marketing === true;
}

/** Whether a one-shot tracking flag has already been claimed for this key. */
export function trackingFlagClaimed(key: string) {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    // localStorage unavailable (private mode etc.): treat as unclaimed.
    return false;
  }
}

/**
 * Claim a one-shot tracking flag. Call this immediately before firing the
 * request it guards, never before a consent check that might still bail, so a
 * declined visitor can still fire on a later consented visit.
 */
export function claimTrackingFlag(key: string) {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    // localStorage unavailable: nothing to claim, caller fires anyway.
  }
}
